import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Settings,
  LogOut,
  Bookmark,
  Heart,
  Bell,
  HelpCircle,
  Shield,
  Home,
  Compass
} from 'lucide-react';

interface ProfileDropdownProps {
  userProfile: {
    username: string;
    full_name: string;
    avatar_url: string;
    role?: string;
  } | null;
  onClose: () => void;
  onSignOut: () => void;
}

export default function ProfileDropdown({ userProfile, onClose, onSignOut }: ProfileDropdownProps) {
  if (!userProfile) return null;

  const MenuItem = ({ to, icon: Icon, label, onClick, className = '' }: {
    to?: string;
    icon: typeof User;
    label: string;
    onClick?: () => void;
    className?: string;
  }) => {
    const content = (
      <>
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </>
    );

    if (to) {
      return (
        <Link
          to={to}
          className={`flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 ${className}`}
          onClick={onClick}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={`flex items-center gap-3 px-4 py-2 text-sm w-full text-left hover:bg-gray-50 ${className}`}
        onClick={onClick}
      >
        {content}
      </button>
    );
  };

  return (
    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
      {/* User Info */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <img
            src={userProfile.avatar_url || '/default-avatar.png'}
            alt={userProfile.username}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {userProfile.full_name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              @{userProfile.username}
            </p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-1">
        <Link
          to={`/perfil/${userProfile.username}`}
          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={onClose}
        >
          <User className="w-4 h-4" />
          <span>Ver Perfil</span>
        </Link>
        {userProfile.role === 'admin' && (
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            <Shield className="w-4 h-4" />
            <span>Administração</span>
          </Link>
        )}
      </div>

      {/* Settings Section */}
      <div className="py-1 border-t border-gray-100">
        <MenuItem
          to="/ajuda"
          icon={HelpCircle}
          label="Ajuda"
          onClick={onClose}
        />
      </div>

      {/* Logout */}
      <div className="py-1 border-t border-gray-100">
        <MenuItem
          icon={LogOut}
          label="Sair"
          onClick={() => {
            onSignOut();
            onClose();
          }}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        />
      </div>
    </div>
  );
}
